import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);

  private isUnauthorizedErrorSubject = new Subject<boolean>();

  constructor() { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.isUnauthorizedErrorSubject.next(true);
          }
          const errorMessage = this.findFirstErrorMessage(error.error);

          if (errorMessage) {
            this.toastService.openSnackBar(
              `An unexpected error occurred: ${errorMessage}`,
              '',
              'error-snackbar',
            );
            console.error('Server Error:', errorMessage);
          }
        } else {
          this.toastService.openSnackBar(
            `An unexpected error occurred. ${error}`,
            '',
            'error-snackbar',
          );
          console.error('Server Error:', error);
        }

        return throwError(() => error);
      }),
    );
  }

  // This method should be called when you want to reset the flag (e.g., after refreshing the token)
  resetUnauthorizedErrorFlag() {
    this.isUnauthorizedErrorSubject.next(false);
  }

  // This method should be called when you refresh the token successfully
  handleTokenRefreshed() {
    this.isUnauthorizedErrorSubject.next(false);
  }

  private findFirstErrorMessage(errorObject: any): string | null {
    if (!errorObject) return null;
    // If the error object directly contains a string message
    if (typeof errorObject === 'string') return errorObject;
    // If error object is an object, look for the first string property
    /* eslint-disable @typescript-eslint/no-unused-vars */
    for (const [_, value] of Object.entries(errorObject.Errors)) {
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        if (this.checkForErrorMessage(value)) { return this.getErrorMessage(value)}
        // Recursively search for error message in nested objects
        const nestedMessage = this.findFirstErrorMessage(value);
        if (nestedMessage) return nestedMessage;
      }
    }
    // If no string found, return null
    return null;
  }

  private checkForErrorMessage(message: any): boolean {
    return !!message.Message;
  }

  private getErrorMessage(message: any): string {
    return message.Message;
  }
}
